// =====================================================
// PAGE: Tableau de bord
// Description: Page d'accueil avec statistiques globales et historique
// =====================================================

import { useState, useMemo, useCallback } from 'react';
import { useNavigate, createSearchParams } from 'react-router-dom';
import { Users, Repeat, CalendarClock, Phone, PhoneOff, Voicemail } from 'lucide-react';
import { UserCallsDetailModal } from '@/components/modals/UserCallsDetailModal';
import { PageHeader } from '@/components/page/PageHeader';
import { StatisticCard } from '@/components/page/StatisticCard';
import { GlobalTimeline } from '@/components/timeline/GlobalTimeline';
import { Card } from '@/components/ui/card';
import { TabNavigation } from '@/components/ui/TabNavigation';
import { Badge } from '@/components/ui/badge';
import { EnterpriseTable } from '@/components/tables/EnterpriseTable';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
  PeriodSelector,
  type PeriodValue,
} from '@/components/ui';
import { useDashboardData } from '@/hooks/useDashboardData';
import { useCallStats } from '@/hooks/useCallStats';
import { usePermissions } from '@/hooks/usePermissions';
import { StatisticsDashboard } from '@/components/dashboard-stats/StatisticsDashboard';
import { Bar, BarChart, XAxis, YAxis, CartesianGrid } from 'recharts';
import type { ColumnDef } from '@tanstack/react-table';
import type { CallStatsByUser } from '@/hooks/useCallStats';
import { ROUTES } from '@/lib/routes';

/**
 * Composant custom pour afficher les labels du graphique sur plusieurs lignes
 */
function CustomXAxisTick({ x, y, payload }: any) {
  const lines = payload.value.split(' / ');

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} textAnchor="middle" fill="#71717a" fontSize={10}>
        {lines.map((line: string, index: number) => (
          <tspan key={index} x={0} dy={index === 0 ? 8 : 12}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
}

/**
 * Page Tableau de bord avec 3 onglets :
 * - Globale (activité récente + tâches urgentes)
 * - Statistiques (placeholder pour graphiques)
 * - Statistiques d'appel (placeholder)
 */
function HomePage() {
  const navigate = useNavigate();
  const { permissions } = usePermissions();

  // Vérifier si l'utilisateur peut voir les statistiques d'appels (admin/superadmin/asso)
  const canViewCallStats =
    permissions?.role === 'admin' ||
    permissions?.role === 'superadmin' ||
    permissions?.role === 'user';

  // États pour les périodes des statistiques d'appels
  const [callsChartPeriod, setCallsChartPeriod] = useState<PeriodValue>('6m');
  const [callsTablePeriod, setCallsTablePeriod] = useState<PeriodValue>('30d');

  // ✅ OPTIMISATION : Un seul hook pour charger toutes les données
  const { stats, contactsByStatus, crews, callsByMonth, loading, loadingCalls } = useDashboardData({
    callsPeriod: callsChartPeriod,
    skipCallStats: !canViewCallStats, // Ne pas charger les stats d'appels pour les users
  });
  // ✅ Passer les crews déjà chargés pour éviter une requête
  const {
    callStats,
    loading: loadingCallStats,
    getCallsByUserId,
  } = useCallStats(crews, {
    period: callsTablePeriod,
  });
  const [activeTab, setActiveTab] = useState('globale');

  // État pour la modal de détail des appels
  const [selectedUser, setSelectedUser] = useState<CallStatsByUser | null>(null);
  const [isCallsModalOpen, setIsCallsModalOpen] = useState(false);
  // Item #17 : permet d'ouvrir la modal pré-filtrée sur un type d'issue d'appel
  const [selectedOutcome, setSelectedOutcome] = useState<
    'answered' | 'no_answer' | 'voicemail' | undefined
  >(undefined);

  const handleCrewsClick = () => {
    navigate(ROUTES.DATA.CREWS.LIST);
  };

  const handleProspectsClick = () => {
    navigate(ROUTES.DATA.PROSPECTS);
  };

  // Bug 4 fix-tests-empiriques (R13) : la carte "Tâches planifiées" du dashboard
  // n'avait pas de onClick (contrairement à Prospects et Taux conversion), créant
  // un faux bug "carré stats non fonctionnel". Désormais elle navigue vers /tasks.
  const handleTasksClick = () => {
    navigate(ROUTES.DASHBOARD.TASKS);
  };

  /**
   * Gestion du clic sur une barre du graphique Vue d'ensemble
   * Redirige vers la page appropriée avec les filtres correspondants
   */
  const handleChartBarClick = useCallback(
    (data: any) => {
      const status = data?.status as string;
      if (!status) return;

      // r-dash-align : le graphique compte désormais les CONTACTS par
      // `contacts.status`. Le clic doit donc mener à la LISTE CONTACTS filtrée
      // par ce statut (et non la liste équipages, ce qui ré-introduirait l'écart
      // que ce fix supprime). `Prospect` → page Prospects. Les barres agrégées
      // (Annulation/Report = 2 statuts) ouvrent la liste contacts sans pré-filtre
      // (la liste n'a pas d'onglet agrégé). Depuis 00232, « Pré-inscrit »
      // (pre_registered + bed_downloaded) et « BED incomplet » (bed_incomplete +
      // bed_incomplete_payment) sont dans le même cas : agrégées, donc sans
      // pré-filtre — sinon le compteur et la liste ouverte divergeraient.
      const statusMapping: Record<string, { route: string; status?: string }> = {
        Prospect: { route: ROUTES.DATA.PROSPECTS },
        'Pré-inscrit': { route: ROUTES.DATA.CONTACTS.LIST },
        'BED incomplet': { route: ROUTES.DATA.CONTACTS.LIST },
        'BED complet': { route: ROUTES.DATA.CONTACTS.LIST, status: 'bed_complete' },
        Annulation: { route: ROUTES.DATA.CONTACTS.LIST },
        Report: { route: ROUTES.DATA.CONTACTS.LIST },
        'Inscrit définitif': { route: ROUTES.DATA.CONTACTS.LIST, status: 'completed' },
      };

      const mapping = statusMapping[status];
      if (!mapping) return;

      // Construire l'URL avec le statut en query param (lu par ContactsListPage)
      if (mapping.status) {
        const searchParams = createSearchParams({ status: mapping.status });
        navigate(`${mapping.route}?${searchParams.toString()}`);
      } else {
        navigate(mapping.route);
      }
    },
    [navigate]
  );

  // Ouvrir la modal de détail des appels pour un utilisateur (clic ligne = aucun filtre)
  const handleUserRowClick = useCallback((user: CallStatsByUser) => {
    setSelectedUser(user);
    setSelectedOutcome(undefined);
    setIsCallsModalOpen(true);
  }, []);

  // Item #17 : ouvrir la modal pré-filtrée par type d'appel via clic sur un badge
  const handleCallStatBadgeClick = useCallback(
    (user: CallStatsByUser, outcome: 'answered' | 'no_answer' | 'voicemail') => {
      setSelectedUser(user);
      setSelectedOutcome(outcome);
      setIsCallsModalOpen(true);
    },
    []
  );

  // Fermer la modal
  const handleCloseCallsModal = useCallback(() => {
    setIsCallsModalOpen(false);
    setSelectedUser(null);
    setSelectedOutcome(undefined);
  }, []);

  // Récupérer les appels de l'utilisateur sélectionné
  const selectedUserCalls = useMemo(() => {
    if (!selectedUser) return [];
    return getCallsByUserId(selectedUser.user_id);
  }, [selectedUser, getCallsByUserId]);

  // Configuration des onglets (stats d'appels uniquement pour admin/superadmin)
  const tabs = useMemo(
    () => [
      { id: 'globale', label: 'Globale' },
      { id: 'statistiques', label: 'Statistiques' },
      ...(canViewCallStats ? [{ id: 'stats-appels', label: "Statistiques d'appel" }] : []),
    ],
    [canViewCallStats]
  );

  // Configuration des couleurs pour les graphiques
  const chartConfig: ChartConfig = useMemo(
    () => ({
      count: {
        label: 'Nombre',
        color: 'hsl(168, 56%, 46%)', // Couleur teal comme dans le screenshot
      },
    }),
    []
  );

  // Configuration pour le graphique des appels (couleurs dynamiques par utilisateur)
  const callsChartConfig: ChartConfig = useMemo(() => {
    const config: ChartConfig = {};
    // Ajouter une couleur pour chaque utilisateur trouvé dans les données
    if (callsByMonth.length > 0) {
      const userKeys = Object.keys(callsByMonth[0]).filter((key) => key !== 'label');
      const colors = ['#10b981', '#f97316', '#3b82f6', '#a855f7', '#ec4899']; // Vert, Orange, Bleu, Violet, Rose
      userKeys.forEach((user, index) => {
        config[user] = {
          label: user,
          color: colors[index % colors.length],
        };
      });
    }
    return config;
  }, [callsByMonth]);

  // Colonnes pour le tableau des statistiques d'appels
  const callStatsColumns = useMemo<ColumnDef<CallStatsByUser>[]>(
    () => [
      {
        id: 'user_name',
        header: 'Nom',
        accessorKey: 'user_name',
      },
      {
        id: 'crews_count',
        header: "Nombres d'équipages",
        accessorKey: 'crews_count',
      },
      {
        id: 'calls',
        header: 'Statistique',
        cell: ({ row }) => {
          const stopAndOpen = (
            e: React.MouseEvent,
            outcome: 'answered' | 'no_answer' | 'voicemail'
          ) => {
            // Empêcher le onRowClick parent (qui ouvrirait sans filtre)
            e.stopPropagation();
            handleCallStatBadgeClick(row.original, outcome);
          };
          return (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={(e) => stopAndOpen(e, 'answered')}
                title="Voir les appels répondus"
                data-testid="call-stat-badge-answered"
                className="cursor-pointer rounded-md transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400"
              >
                <Badge
                  variant="outline"
                  className="gap-1 border-green-200 bg-green-50 text-green-700"
                >
                  <Phone className="h-3 w-3" />
                  {row.original.answered_calls}
                </Badge>
              </button>
              <button
                type="button"
                onClick={(e) => stopAndOpen(e, 'no_answer')}
                title="Voir les appels sans réponse"
                data-testid="call-stat-badge-no_answer"
                className="cursor-pointer rounded-md transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
              >
                <Badge variant="outline" className="gap-1 border-red-200 bg-red-50 text-red-700">
                  <PhoneOff className="h-3 w-3" />
                  {row.original.no_answer_calls}
                </Badge>
              </button>
              <button
                type="button"
                onClick={(e) => stopAndOpen(e, 'voicemail')}
                title="Voir les appels avec messagerie"
                data-testid="call-stat-badge-voicemail"
                className="cursor-pointer rounded-md transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
              >
                <Badge
                  variant="outline"
                  className="gap-1 border-amber-200 bg-amber-50 text-amber-700"
                >
                  <Voicemail className="h-3 w-3" />
                  {row.original.voicemail_calls}
                </Badge>
              </button>
            </div>
          );
        },
      },
    ],
    [handleCallStatBadgeClick]
  );

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex items-center gap-2 text-zinc-500">
          <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-zinc-900"></div>
          <span>Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-neutral-50">
      {/* Breadcrumb - Toujours visible en haut, pleine largeur */}
      <PageHeader breadcrumbs={[{ label: 'Tableau de bord' }]} />

      {/* Titre - Pleine largeur */}
      <div className="border-t border-b border-zinc-200 bg-white px-6 py-6">
        <h1 className="text-xl font-semibold text-zinc-900">Tableau de bord</h1>
      </div>

      {/* Navigation par onglets - Pleine largeur */}
      <div className="border-b border-zinc-200 bg-white px-6">
        <TabNavigation tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      </div>

      {/* Contenu selon l'onglet actif */}
      {/* Item #0 : overflow-y-auto pour permettre le scroll vertical jusqu'en bas
          (sinon les cards stackées dépassent et le bas est coupé). */}
      <div className="flex flex-1 overflow-y-auto p-6">
        {/* Onglet Globale : Layout 60/40 avec cards + contenu | timeline */}
        {activeTab === 'globale' && (
          <div className="flex flex-1 gap-6 overflow-hidden">
            {/* Colonne gauche : 60% */}
            <div className="flex flex-1 flex-col gap-6 overflow-hidden">
              {/* Cards statistiques */}
              <div className="grid grid-cols-3 gap-6">
                <StatisticCard
                  title="Prospects"
                  value={stats.totalProspects}
                  description={`${stats.totalProspects} Prospects • ${stats.totalCrews} Équipages`}
                  icon={Users}
                  onClick={handleProspectsClick}
                />
                <StatisticCard
                  title="Taux conversion"
                  value={`${stats.conversionRate}%`}
                  description={`${stats.totalCrews} Équipages`}
                  icon={Repeat}
                  onClick={handleCrewsClick}
                />
                <StatisticCard
                  title="Tâches planifiées"
                  value={stats.totalPendingTasks}
                  description={`${stats.urgentTasksCount} Urgentes`}
                  icon={CalendarClock}
                  onClick={handleTasksClick}
                />
              </div>

              {/* Card 1: Vue d'ensemble */}
              <Card className="flex flex-1 flex-col overflow-hidden p-6">
                <h3 className="mb-4 text-lg font-semibold text-zinc-900">Vue d'ensemble</h3>
                <ChartContainer config={chartConfig} className="h-full w-full">
                  <BarChart
                    data={contactsByStatus}
                    margin={{ top: 10, right: 20, left: 10, bottom: 50 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="status"
                      tickLine={false}
                      axisLine={false}
                      height={50}
                      tick={<CustomXAxisTick />}
                      interval={0}
                    />
                    <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
                    <ChartTooltip
                      content={<ChartTooltipContent />}
                      wrapperStyle={{ maxWidth: 280, zIndex: 50 }}
                      allowEscapeViewBox={{ x: false, y: false }}
                    />
                    <Bar
                      dataKey="count"
                      fill="hsl(168, 56%, 46%)"
                      radius={[4, 4, 0, 0]}
                      cursor="pointer"
                      onClick={(data) => handleChartBarClick(data)}
                    />
                  </BarChart>
                </ChartContainer>
              </Card>

              {/* Card 2: Statistiques d'appels (admin/superadmin uniquement) */}
              {canViewCallStats && (
                <Card className="flex flex-1 flex-col overflow-hidden p-6">
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-zinc-900">Statistiques d'appels</h3>
                    <PeriodSelector value={callsChartPeriod} onChange={setCallsChartPeriod} />
                  </div>
                  {loadingCalls ? (
                    <div className="flex flex-1 items-center justify-center">
                      <div className="flex items-center gap-2 text-zinc-500">
                        <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-zinc-900"></div>
                        <span>Chargement...</span>
                      </div>
                    </div>
                  ) : (
                    <ChartContainer config={callsChartConfig} className="h-full w-full">
                      <BarChart
                        data={callsByMonth}
                        margin={{ top: 10, right: 20, left: 10, bottom: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis
                          dataKey="label"
                          tickLine={false}
                          axisLine={false}
                          tick={{ fontSize: 11 }}
                        />
                        <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
                        <ChartTooltip
                          content={<ChartTooltipContent />}
                          wrapperStyle={{ maxWidth: 280, zIndex: 50 }}
                          allowEscapeViewBox={{ x: false, y: false }}
                        />
                        <ChartLegend content={<ChartLegendContent />} />
                        {Object.keys(callsChartConfig).map((key) => (
                          <Bar
                            key={key}
                            dataKey={key}
                            stackId="a"
                            fill={callsChartConfig[key].color}
                            radius={[4, 4, 0, 0]}
                          />
                        ))}
                      </BarChart>
                    </ChartContainer>
                  )}
                </Card>
              )}
            </div>

            {/* Colonne droite : 40% - Timeline globale */}
            <div className="w-[40%] overflow-hidden">
              <GlobalTimeline />
            </div>
          </div>
        )}

        {/* Onglet Statistiques : dashboard stats natif (RETOUCHE 2 — déplacé
            depuis l'ancienne page dédiée /statistiques). L'ancien bloc « Modes de
            paiement par projet » est remplacé par ce dashboard complet. */}
        {activeTab === 'statistiques' && <StatisticsDashboard />}

        {/* Onglet Statistiques d'appel : Tableau des stats par utilisateur (admin/superadmin uniquement) */}
        {activeTab === 'stats-appels' && canViewCallStats && (
          <div className="flex flex-1 flex-col overflow-hidden bg-card text-card-foreground rounded-lg border shadow-sm pb-4">
            <div className="flex items-center justify-between border-b border-zinc-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-zinc-900">
                Statistiques d'appels par utilisateur
              </h3>
              <PeriodSelector value={callsTablePeriod} onChange={setCallsTablePeriod} />
            </div>
            <div className="flex-1 overflow-auto px-6 py-6">
              {loadingCallStats ? (
                <div className="flex h-full items-center justify-center">
                  <div className="flex items-center gap-2 text-zinc-500">
                    <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-zinc-900"></div>
                    <span>Chargement des statistiques...</span>
                  </div>
                </div>
              ) : (
                <EnterpriseTable
                  data={callStats}
                  columns={callStatsColumns}
                  onRowClick={handleUserRowClick}
                  // Item #12 : afficher tous les users sans pagination silencieuse à 10
                  pageSize={Math.max(callStats.length, 10)}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal de détail des appels d'un utilisateur */}
      {selectedUser && (
        <UserCallsDetailModal
          isOpen={isCallsModalOpen}
          onClose={handleCloseCallsModal}
          userName={selectedUser.user_name}
          userId={selectedUser.user_id}
          calls={selectedUserCalls}
          period={callsTablePeriod}
          initialOutcomeFilter={selectedOutcome}
        />
      )}
    </div>
  );
}

export default HomePage;
